/* eslint-disable no-underscore-dangle,no-await-in-loop */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { useCallback, useState } from 'react'
import prepareHeaders from '../utils/prepareHeaders'
import { delay } from '../utils/utils'

export const HUB_TASK_STATUS = {
  SUCCESS: 'SUCCESS',
  FAIL: 'FAILED',
}

export const getHubErrorMessage = (data) => {
  const errorsJsonStr = data?._source?.errors

  let message = 'Failed to perform action'
  try {
    const errors = JSON.parse(errorsJsonStr)
    message =
      errors?.[0]?.innerError?.message || errors?.[0]?.message?.Message || errors?.[0]?.message || String(errorsJsonStr)
  } catch (e) {
    // ignore
  }

  return message
}

export const hubApi = createApi({
  reducerPath: 'hub',
  baseQuery: fetchBaseQuery({
    prepareHeaders,
    baseUrl: process.env.REACT_APP_AUGMENTT_API_URL,
  }),
  endpoints: (builder) => ({
    getTaskStatus: builder.query({
      query: (taskId) => ({
        url: `/hub/${taskId}/status`,
      }),
      transformResponse: (data) => {
        if (data?._source?.status === HUB_TASK_STATUS.FAIL) {
          throw new Error(getHubErrorMessage(data))
        }
        return data
      },
    }),
  }),
})

export const { useGetTaskStatusQuery, useLazyGetTaskStatusQuery } = hubApi

export const usePollTaskStatus = () => {
  const maxRetries = 180

  const [isFetching, setIsFetching] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isError, setIsError] = useState(false)
  const [error, setError] = useState(null)
  const [getTaskStatus, result] = useLazyGetTaskStatusQuery()

  const pollTaskStatus = useCallback(
    async (taskId) => {
      setIsFetching(true)
      setIsSuccess(false)
      setIsError(false)
      setError(null)
      let status = null
      let pollResult = null
      let retries = 0

      try {
        do {
          await delay(5 * 1000)
          pollResult = await getTaskStatus(taskId)
          status = pollResult?.data?._source?.status
          retries += 1
        } while (!pollResult.isError && status !== HUB_TASK_STATUS.SUCCESS && retries < maxRetries)
      } catch (err) {
        // any error should be handled by rtk query internally, if there is any unexpected error just stop execution, log and return
        console.error(err)
      }

      const taskSuccess = status === HUB_TASK_STATUS.SUCCESS
      const timedOut = !taskSuccess && !pollResult?.isError
      const resolvedError = timedOut ? { message: 'Task timed out waiting for completion' } : pollResult?.error
      setIsSuccess(taskSuccess)
      setIsError(!taskSuccess)
      setError(resolvedError)
      setIsFetching(false)
      return { ...pollResult, isSuccess: taskSuccess, isError: !taskSuccess, error: resolvedError }
    },
    [getTaskStatus],
  )

  return { ...result, isSuccess, isError, error, pollTaskStatus, isFetching }
}
